import {
  IInvoiceHistoryMetadata,
  InvoiceHistoryResponse,
} from "../typings/interfaces/responses/invoice-history-response";
import { formatUS12HourTime, formatUSDate } from "./common-functions";

// What a PIN_TAG_UPDATED history card shows: colour and tags (one value when
// unchanged, old → new when changed), plus Special / Regular and the ready
// date when those changed. Events saved before those fields existed only
// carry colour and tags.

export interface IPinTagChangeView {
  colorChanged: boolean;
  tagsChanged: boolean;
  fromTags: string;
  toTags: string;
  service?: { from: string; to: string };
  ready?: { from: string; to: string };
}

const tagsLabel = (tags?: string[]) => (tags && tags.length > 0 ? tags.join(", ") : "—");

const sameTags = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

const serviceLabel = (special?: boolean | null) => (special ? "Special" : "Regular");

/** "2026-10-30T15:00" (store wall-clock) → "10/30/2026 3:00 PM" in the store's format */
export const readyLabel = (ready?: string | null) => {
  if (!ready) return "—";
  const date = new Date(ready);
  return isNaN(date.getTime()) ? ready : `${formatUSDate(date)} ${formatUS12HourTime(date)}`;
};

export const pinTagChangeView = (metadata: IInvoiceHistoryMetadata): IPinTagChangeView => {
  const view: IPinTagChangeView = {
    colorChanged: (metadata.fromColor ?? null) !== (metadata.toColor ?? null),
    tagsChanged: !sameTags(metadata.fromTags, metadata.toTags),
    fromTags: tagsLabel(metadata.fromTags),
    toTags: tagsLabel(metadata.toTags),
  };
  if (
    metadata.fromSpecial !== undefined &&
    metadata.toSpecial !== undefined &&
    !!metadata.fromSpecial !== !!metadata.toSpecial
  ) {
    view.service = { from: serviceLabel(metadata.fromSpecial), to: serviceLabel(metadata.toSpecial) };
  }
  if ((metadata.fromReady || metadata.toReady) && metadata.fromReady !== metadata.toReady) {
    view.ready = { from: readyLabel(metadata.fromReady), to: readyLabel(metadata.toReady) };
  }
  return view;
};

/**
 * Whether the ticket was Special or Regular, for the Order Created and Order
 * Edited cards (null on other cards). Those events carry a "RUSH" tag when the
 * ticket was Special at that moment; a Created event saved before the tag also
 * has the check-in pin tag's tab in its bill snapshot.
 */
export const ticketServiceOf = (
  event: Pick<InvoiceHistoryResponse, "type" | "title" | "metadata">
): "Special" | "Regular" | null => {
  const isCreated = event.type === "CREATED" || event.title === "Order Created";
  const isEdited = event.type === "EDITED" || event.title === "Order Edited";
  if (!isCreated && !isEdited) return null;
  if (event.metadata?.tags?.includes("RUSH")) return "Special";
  if (isCreated && event.metadata?.originalBillSnapshot) {
    try {
      const session = JSON.parse(event.metadata.originalBillSnapshot);
      if (session?.pinTag?.isSpecial === true || session?.isSpecial === true) return "Special";
    } catch {
      // Not a ticket snapshot: go by the tag alone
    }
  }
  return "Regular";
};
